import { resolveTerms } from "./resolveTerms";

/**
 * Tag ids for whatever terms a watchlist holds.
 *
 * Every route into a watchlist should produce the same record, and the part
 * that matters is the tag: `transformSearchParams` sends a list's tags
 * alongside its tickers and keywords under filter_mode OR, so a tagged list
 * matches precisely on any feed carrying tags while an untagged one is
 * reduced to text matching. Two lists holding the same names behaved
 * differently depending on which screen made them.
 *
 * Deliberately tags only. It does not move a term between `tickers` and
 * `keywords`, because on an edit that would silently rearrange a list the user
 * has already arranged — someone who put "OPEC" in keywords meant it there.
 * The builder and the onboarding portfolio step no longer look terms up at
 * all — symbols go in as written and tags are picked on purpose, in the Tags
 * box (builderModel.ts) — so this serves the surfaces that still want the
 * tag half.
 *
 * Never throws. A watchlist without tags is worth far more than a save that
 * failed because a lookup did, so an unreachable tag service yields an empty
 * list and the record is written as the user typed it.
 */
export async function tagsForTerms(
  terms: string[],
  signal?: AbortSignal
): Promise<string[]> {
  const byTerm = await tagsByTerm(terms, signal);
  return [...new Set(byTerm.values())];
}

/**
 * The same lookup, but keyed by the term it came from.
 *
 * Exists so a caller creating SEVERAL watchlists at once can resolve every
 * term in one pass and then hand each list its own tags, instead of paying a
 * full round of lookups per list in series. `resolveTerms` already dedupes,
 * so overlapping lists get cheaper rather than more expensive.
 *
 * Never throws, for the same reason `tagsForTerms` doesn't.
 */
export async function tagsByTerm(
  terms: string[],
  signal?: AbortSignal
): Promise<Map<string, string>> {
  const unique = [...new Set(terms.map((t) => t.trim()).filter(Boolean))];
  if (unique.length === 0) return new Map();

  const { resolved } = await resolveTerms(unique, { signal }).catch(() => ({
    resolved: [],
  }));

  const byTerm = new Map<string, string>();
  for (const term of resolved) {
    if (term.tagId != null) byTerm.set(term.input, String(term.tagId));
  }
  return byTerm;
}
